/*
 * Preview — Dropship Payment Policy (Autopay / Prepay / Manual)
 *
 * Visual mockup of the proposed UI. No backend calls. Hit
 * /preview/payment-policy to view.
 *
 * Covers the 3 payment policies + the 2 automation modes the user asked to
 * confirm — plus the surfaces that change:
 *   1. Agreement Settings drawer → new "Payment" section
 *   2. Daily Orders awaiting_payment row with specific reason chip
 *   3. Auto-Invoices row with autopay_status chip + "Prepay now" button
 */
import { useState } from "react";
import {
  Landmark, Wallet, Clock, ShieldCheck, Lock, AlertTriangle,
  Check, ArrowRight, Zap, FileSignature, Info, Receipt, CalendarClock,
  Building2, ChevronRight, Sparkles,
} from "lucide-react";

// Four scenarios the user described.
const SCENARIOS = {
  credit_autopay: {
    label: "Credit active · Autopay on due date",
    sub: "Buyer has approved Net-30. Orders ship freely; daily invoice auto-debits ACH on due date.",
    coverage: "net_terms",
    policy: "autopay_on_due_date",
    accent: "emerald",
  },
  prepay_per_order: {
    label: "No credit · Prepay per order (ACH)",
    sub: "No credit line. ACH charged on each order before ship. Full Auto only ships if ACH approves.",
    coverage: "none",
    policy: "prepay_per_order",
    accent: "amber",
  },
  wallet_only: {
    label: "No credit · Wallet only",
    sub: "Buyer funds a wallet. FIFO ship until wallet runs out; then pause & prompt top-up.",
    coverage: "wallet",
    policy: "prepay_per_order",
    accent: "amber",
  },
  manual: {
    label: "Daily Confirm · Manual payment",
    sub: "Buyer confirms each day's orders and pays via ACH on file. Ship only after payment clears.",
    coverage: "none",
    policy: "manual_per_invoice",
    accent: "rose",
  },
};

export default function PaymentPolicyPreview() {
  const [scenario, setScenario] = useState("credit_autopay");
  const [policy, setPolicy] = useState(SCENARIOS.credit_autopay.policy);
  const [automation, setAutomation] = useState("full_auto"); // full_auto | daily_confirm
  const s = SCENARIOS[scenario];
  const hasCredit = s.coverage === "net_terms";
  const hasWallet = s.coverage === "wallet";

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-[1400px] mx-auto px-8 py-5 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Preview · internal</div>
            <div className="display text-xl mt-0.5">Dropship payment policy</div>
          </div>
          <div className="ml-auto flex items-center gap-1 rounded-full border border-border bg-white p-1" data-testid="scenario-switcher">
            {Object.entries(SCENARIOS).map(([k, v]) => (
              <button key={k}
                onClick={() => { setScenario(k); setPolicy(v.policy); }}
                className={`rounded-full px-4 h-9 text-[11.5px] mono uppercase tracking-[0.14em] transition-colors ${scenario === k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                {v.label.split(" · ")[0]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 py-8 space-y-10">
        {/* Scenario summary */}
        <div className={`rounded-2xl border p-5 flex items-start gap-4 ${
          s.accent === "emerald" ? "bg-emerald-50/60 border-emerald-200" :
          s.accent === "amber"   ? "bg-amber-50/60 border-amber-200" :
                                    "bg-rose-50/60 border-rose-200"
        }`}>
          <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
            s.accent === "emerald" ? "bg-emerald-600 text-white" :
            s.accent === "amber"   ? "bg-amber-500 text-white" :
                                      "bg-rose-600 text-white"
          }`}>
            {s.coverage === "net_terms" ? <ShieldCheck className="w-5 h-5"/> :
             s.coverage === "wallet"    ? <Wallet className="w-5 h-5"/> :
                                           <AlertTriangle className="w-5 h-5"/>}
          </div>
          <div className="flex-1">
            <div className="text-[11px] mono uppercase tracking-[0.16em] text-foreground/55">Scenario</div>
            <div className="display text-lg mt-0.5">{s.label}</div>
            <div className="text-[12.5px] text-foreground/65 mt-1 max-w-3xl">{s.sub}</div>
          </div>
          <div className="flex gap-1.5 text-[10.5px] mono uppercase tracking-[0.16em]">
            <span className={`px-2 py-1 rounded-full border ${hasCredit ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-foreground/5 text-foreground/50 border-border"}`}>Net-30 · {hasCredit ? "on" : "off"}</span>
            <span className="px-2 py-1 rounded-full border bg-foreground/5 text-foreground/50 border-border">Insurance · off</span>
            <span className={`px-2 py-1 rounded-full border ${hasWallet ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-foreground/5 text-foreground/50 border-border"}`}>Wallet · {hasWallet ? "$2,400" : "$0"}</span>
          </div>
        </div>

        {/* ═══ 1. Agreement Settings Drawer → Payment section ═══ */}
        <Section
          n={1}
          title="Agreement settings → NEW Payment section"
          sub="Added below Returns in the buyer's ⚙ Settings drawer. Bilateral, buyer-owned. Seller sees read-only.">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-5">
            <MockDrawer>
              <div className="rounded-xl border border-border bg-white p-5 space-y-4" data-testid="payment-policy-editor">
                <div>
                  <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Payment</div>
                  <div className="text-[12.5px] text-foreground/70 mt-0.5 max-w-xl">
                    How does the buyer settle invoices for this supplier? Autopay keeps the relationship frictionless once credit is active.
                  </div>
                </div>

                <div className="space-y-2">
                  <PolicyCard
                    k="autopay_on_due_date"
                    icon={CalendarClock}
                    active={policy === "autopay_on_due_date"}
                    disabled={!hasCredit}
                    onClick={() => setPolicy("autopay_on_due_date")}
                    title="Autopay on due date · ACH"
                    sub="Daily invoices auto-debit via ACH on the due date. Prepay option always available."
                    badge={hasCredit ? null : { label: "Locked · needs credit", tone: "locked" }}
                  />
                  <PolicyCard
                    k="prepay_per_order"
                    icon={Zap}
                    active={policy === "prepay_per_order"}
                    onClick={() => setPolicy("prepay_per_order")}
                    title="Prepay per order · ACH / wallet"
                    sub="Each order charges your default payment method before shipping. Ship only if charge approves."
                  />
                  <PolicyCard
                    k="manual_per_invoice"
                    icon={FileSignature}
                    active={policy === "manual_per_invoice"}
                    onClick={() => setPolicy("manual_per_invoice")}
                    title="Manual — pay each invoice"
                    sub="Buyer reviews and pays each daily invoice manually. Use only for low-volume relationships."
                  />
                </div>

                {/* ACH method picker — uses saved methods from Payments portal */}
                {policy !== "manual_per_invoice" && (
                  <div className="pt-3 border-t border-border space-y-2" data-testid="ach-method-picker">
                    <div className="flex items-center justify-between">
                      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Autopay method for this supplier</div>
                      <a href="/buyer/payments/methods" className="text-[11px] text-foreground/55 underline hover:text-foreground inline-flex items-center gap-1">
                        Manage methods <ArrowRight className="w-3 h-3"/>
                      </a>
                    </div>
                    <div className="relative">
                      <select defaultValue="ach_1" className="w-full h-11 rounded-lg border border-border bg-white pl-10 pr-3 text-[13px] appearance-none focus:outline-none focus:ring-2 focus:ring-foreground/10">
                        <option value="ach_1">JPMorgan Chase · ····4821 (default)</option>
                        <option value="ach_2">First Republic · ····9092</option>
                      </select>
                      <Landmark className="w-4 h-4 text-foreground/55 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"/>
                      <ChevronRight className="w-4 h-4 text-foreground/55 absolute right-3 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none"/>
                    </div>
                    <div className="text-[11px] text-foreground/55">
                      Only ACH bank accounts from your <a href="/buyer/payments/methods" className="underline hover:text-foreground">saved payment methods</a> can be used for autopay. Cards aren't eligible.
                    </div>
                  </div>
                )}

                {/* Contextual warnings */}
                {policy === "autopay_on_due_date" && !hasCredit && (
                  <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-900 flex gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
                    <div>
                      <strong>Autopay requires an approved credit line.</strong> Until credit is granted, orders will follow <em>Prepay per order</em> instead. <a href="#" className="underline">Apply for Net-30</a>.
                    </div>
                  </div>
                )}
                {policy === "prepay_per_order" && (
                  <div className="rounded-xl border border-border bg-secondary/50 p-3 text-[12.5px] text-foreground/75 flex gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5"/>
                    <div>
                      Each order charges this ACH method before the seller can ship. Typical settle time: <strong>1–2 business days</strong>. If ACH fails, the order waits in <em>Awaiting payment</em>.
                    </div>
                  </div>
                )}
                {policy === "manual_per_invoice" && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-900 flex gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
                    <div>
                      Manual-pay creates shipping delays if invoices sit unpaid. You'll be notified every day an invoice is past due.
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-3 border-t border-border">
                  <button className="rounded-full border border-border h-9 px-4 text-[12.5px] hover:bg-secondary">Cancel</button>
                  <button className="rounded-full bg-foreground text-background h-9 px-4 text-[12.5px] hover:bg-foreground/90 ml-auto">Save payment policy</button>
                </div>
              </div>
            </MockDrawer>

            {/* Effects summary */}
            <div className="rounded-2xl border border-border bg-white p-5 h-fit space-y-4">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">What this policy does</div>
              <EffectRow
                when="When a customer order comes in"
                then={automation === "full_auto"
                  ? (policy === "autopay_on_due_date" && hasCredit
                      ? "Auto-ships immediately (credit covers it)"
                      : policy === "prepay_per_order"
                        ? (hasWallet ? "Auto-debits ACH → ships if approved. If ACH fails, wallet falls back."
                                      : "Auto-debits ACH → ships if approved. If fail, parks as Awaiting payment.")
                        : "Buyer must manually pay the daily invoice before seller can re-ship")
                  : (policy === "autopay_on_due_date" && hasCredit
                      ? "Buyer still confirms daily; credit covers it post-ship"
                      : "Buyer confirms + ACH charges before seller ships")}
              />
              <EffectRow
                when="When the daily invoice is generated"
                then={policy === "autopay_on_due_date"
                  ? "Scheduled for ACH debit on the due date (Net-30)"
                  : policy === "prepay_per_order"
                    ? "Invoice issued as a receipt — already paid per-order"
                    : "Buyer gets a bell · must manually pay before the next invoice window"}
              />
              <EffectRow
                when="When the due date arrives"
                then={policy === "autopay_on_due_date"
                  ? "ACH debit fires automatically. 3 retry attempts over 5 days on failure, then shipping halts."
                  : policy === "prepay_per_order"
                    ? "N/A — already settled"
                    : "Past-due email + shipping pause until buyer pays"}
              />
              <EffectRow
                when="Prepay option"
                then={policy === "autopay_on_due_date"
                  ? "'Prepay now' button on every issued invoice (buyer can pay early)"
                  : policy === "prepay_per_order"
                    ? "Built-in — every order is prepaid"
                    : "Yes — buyer can pay any time before due date"}
              />
            </div>
          </div>
        </Section>

        {/* ═══ 2. Daily Orders — awaiting_payment row states ═══ */}
        <Section
          n={2}
          title="Daily Orders — awaiting_payment row with payment reason"
          sub="When Full Auto fails to pay, or Daily Confirm needs buyer action, the row now explains WHY and offers the right CTA inline.">
          <div className="rounded-2xl border border-border bg-white divide-y divide-border overflow-hidden">
            <OrderRow
              code="DS-88921"
              customer="Sarah Patel · 241 Palmer Ave"
              product="Glow Serum × 2"
              total="$86.40"
              stateChip={{ label: "Awaiting payment · ACH pending", tone: "amber" }}
              reason="ACH debit submitted 14h ago. Auto-retry if not cleared within 48h. Seller blocked from shipping."
              cta={null}
            />
            <OrderRow
              code="DS-88922"
              customer="Marcus Lee · 2290 Kenmore"
              product="Bloom Duo × 1"
              total="$34.00"
              stateChip={{ label: "Awaiting payment · wallet short", tone: "rose" }}
              reason="Wallet balance $0 · needs $34.00 to unblock. Added to FIFO queue."
              cta={{ label: "Top up wallet", tone: "foreground" }}
            />
            <OrderRow
              code="DS-88923"
              customer="Ana Ríos · 10 W 9th St"
              product="Eye Cream × 3"
              total="$129.00"
              stateChip={{ label: "Awaiting confirm", tone: "indigo" }}
              reason="Daily Confirm — waiting on buyer approval + payment. Cutoff: 2h 14m."
              cta={{ label: "Confirm & pay", tone: "foreground" }}
            />
            <OrderRow
              code="DS-88924"
              customer="John Pryor · 55 Mott St"
              product="Glow Serum × 1"
              total="$43.20"
              stateChip={{ label: "In transit", tone: "emerald" }}
              reason={null}
              cta={null}
            />
          </div>
        </Section>

        {/* ═══ 3. Auto-Invoices — autopay status chip + Prepay now ═══ */}
        <Section
          n={3}
          title="Auto-Invoices — autopay scheduling chip + Prepay now"
          sub="Every issued invoice shows WHEN ACH will fire, and lets buyers prepay instantly.">
          <div className="rounded-2xl border border-border bg-white divide-y divide-border overflow-hidden">
            <InvoiceRow
              code="DS-INV-20260503-A1B2C3"
              date="May 3 2026"
              amount="$1,284.50"
              autopay={{ status: "scheduled", on: "Jun 2, 2026 · ACH · Chase ····4821" }}
              prepayable
            />
            <InvoiceRow
              code="DS-INV-20260502-D4E5F6"
              date="May 2 2026"
              amount="$902.00"
              autopay={{ status: "succeeded", on: "Jun 1, 2026 · $902.00 debited" }}
              prepayable={false}
            />
            <InvoiceRow
              code="DS-INV-20260501-B7C8D9"
              date="May 1 2026"
              amount="$544.80"
              autopay={{ status: "failed", on: "Retry 2 of 3 · May 31 2026" }}
              prepayable
              alert
            />
            <InvoiceRow
              code="DS-INV-20260430-E0F1G2"
              date="Apr 30 2026"
              amount="$76.00"
              autopay={{ status: "manual", on: "Manual — awaiting buyer payment" }}
              prepayable
            />
          </div>
        </Section>

        {/* ═══ 4. Automation mode · decision matrix ═══ */}
        <Section
          n={4}
          title="Decision matrix — what happens for each combo"
          sub="Snapshot of the full product rules you described — helps confirm the logic before implementation.">
          <div className="rounded-2xl border border-border bg-white overflow-hidden">
            <div className="grid grid-cols-[1fr_1.5fr_1.5fr] text-[11px] mono uppercase tracking-[0.15em] text-foreground/55 bg-secondary/50 border-b border-border">
              <div className="px-4 py-3">Automation / Coverage</div>
              <div className="px-4 py-3">Full Auto</div>
              <div className="px-4 py-3">Daily Confirm</div>
            </div>
            <MatrixRow
              label="Credit active"
              fullAuto="Ship instantly. Invoice autopays via ACH on due date."
              dailyConfirm="Buyer taps Confirm each morning. Invoice autopays on due date."
              tone="emerald"
            />
            <MatrixRow
              label="No credit · ACH on file"
              fullAuto="Charge ACH per-order. Ship if approved. Park in Awaiting payment if fail."
              dailyConfirm="Buyer confirms + ACH charges before seller can ship. Retry on failure."
              tone="amber"
            />
            <MatrixRow
              label="No credit · Wallet only"
              fullAuto="FIFO wallet debit. Ship until wallet runs out, then pause + top-up prompt."
              dailyConfirm="Buyer confirms + wallet debits. Top-up prompt when short."
              tone="amber"
            />
            <MatrixRow
              label="No credit · No wallet · No ACH"
              fullAuto="LOCKED — Full Auto not available. Must configure one first."
              dailyConfirm="Manual invoices — buyer pays each one. Seller ships only after clearing."
              tone="rose"
            />
          </div>
        </Section>

        {/* Footer sign-off */}
        <div className="rounded-2xl border border-foreground bg-foreground text-background p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-background/60">Ready to implement</div>
            <div className="display text-xl mt-1">Approve this design to ship</div>
            <div className="text-[12.5px] text-background/70 mt-1 max-w-2xl">
              Backend: payment_policy on dropship_terms + autopay_status on invoices + APScheduler cron · 30 min interval · ACH rail. Frontend: exactly these 3 surfaces.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] mono uppercase tracking-[0.14em] text-background/50">~6–8h effort</span>
            <ChevronRight className="w-5 h-5 text-background/60"/>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────

function Section({ n, title, sub, children }) {
  return (
    <section className="space-y-3" data-testid={`section-${n}`}>
      <div className="flex items-baseline gap-3">
        <span className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/50">{String(n).padStart(2, "0")}</span>
        <h2 className="display text-2xl tracking-tight">{title}</h2>
      </div>
      <p className="text-[13px] text-foreground/60 max-w-3xl">{sub}</p>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function MockDrawer({ children }) {
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden shadow-sm">
      <div className="px-5 py-3 bg-secondary/40 border-b border-border flex items-center gap-2">
        <Building2 className="w-4 h-4 text-foreground/55"/>
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">⚙ Settings drawer · Agreement</div>
          <div className="text-[12.5px] font-medium">GlowCo Beauty · TEST batch shop</div>
        </div>
      </div>
      <div className="p-5 max-h-[720px] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function PolicyCard({ icon: Icon, active, disabled, onClick, title, sub, badge }) {
  return (
    <button type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`w-full text-left rounded-xl border p-4 transition-all flex items-start gap-3 ${
        active ? "border-foreground bg-foreground text-background" :
        disabled ? "border-border bg-secondary/40 opacity-55 cursor-not-allowed" :
                   "border-border bg-white hover:border-foreground/40"
      }`}>
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${active ? "text-background" : "text-foreground/70"}`}/>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-[13.5px] font-medium">{title}</div>
          {active && <span className="ml-auto text-[10px] mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-background/20">Selected</span>}
          {badge?.tone === "locked" && (
            <span className="text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
              <Lock className="w-2.5 h-2.5"/>{badge.label}
            </span>
          )}
        </div>
        <div className={`text-[12px] mt-1 ${active ? "text-background/70" : "text-foreground/60"}`}>{sub}</div>
      </div>
    </button>
  );
}

function EffectRow({ when, then }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 text-[12.5px]">
      <div className="text-foreground/50 mono uppercase tracking-[0.12em] text-[10.5px] pt-0.5">{when}</div>
      <div className="text-foreground/90">{then}</div>
    </div>
  );
}

function OrderRow({ code, customer, product, total, stateChip, reason, cta }) {
  const tone = stateChip.tone;
  const chipCls =
    tone === "amber"   ? "bg-amber-50 text-amber-800 border-amber-200" :
    tone === "rose"    ? "bg-rose-50 text-rose-700 border-rose-200" :
    tone === "indigo"  ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
    tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          "bg-foreground/5 text-foreground/60 border-border";
  const rowCls =
    tone === "amber"  ? "border-l-4 border-l-amber-300 bg-amber-50/20" :
    tone === "rose"   ? "border-l-4 border-l-rose-300 bg-rose-50/20" :
    tone === "indigo" ? "border-l-4 border-l-indigo-300 bg-indigo-50/20" :
                         "";
  return (
    <div className={`px-5 py-4 grid grid-cols-[1fr_auto] gap-4 items-center ${rowCls}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium">{code}</span>
          <span className={`text-[10.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border ${chipCls}`}>{stateChip.label}</span>
        </div>
        <div className="text-[12.5px] text-foreground/65 mt-0.5 truncate">{customer} · {product}</div>
        {reason && <div className="text-[11.5px] text-foreground/60 mt-1 italic">{reason}</div>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50">Total</div>
          <div className="text-[14px] font-medium">{total}</div>
        </div>
        {cta && (
          <button className="rounded-full h-9 px-4 text-[12px] bg-foreground text-background hover:bg-foreground/90 inline-flex items-center gap-1.5">
            {cta.label}<ArrowRight className="w-3 h-3"/>
          </button>
        )}
      </div>
    </div>
  );
}

function InvoiceRow({ code, date, amount, autopay, prepayable, alert }) {
  const tone = autopay.status === "scheduled"  ? "indigo" :
               autopay.status === "succeeded"  ? "emerald" :
               autopay.status === "failed"     ? "rose" :
                                                  "neutral";
  const chipCls =
    tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    tone === "indigo"  ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
    tone === "rose"    ? "bg-rose-50 text-rose-700 border-rose-200" :
                          "bg-secondary text-foreground/60 border-border";
  const label = autopay.status === "scheduled" ? "Autopay scheduled"
              : autopay.status === "succeeded" ? "Autopay succeeded"
              : autopay.status === "failed"    ? "Autopay failed · retrying"
              :                                   "Manual · unscheduled";
  return (
    <div className={`px-5 py-4 grid grid-cols-[1fr_auto] gap-4 items-center ${alert ? "border-l-4 border-l-rose-400 bg-rose-50/20" : ""}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Receipt className="w-3.5 h-3.5 text-foreground/50"/>
          <span className="text-[13px] font-medium">{code}</span>
          <span className={`text-[10.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border ${chipCls}`}>{label}</span>
          {alert && <span className="text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-rose-600 text-white inline-flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5"/>Action needed</span>}
        </div>
        <div className="text-[12.5px] text-foreground/65 mt-0.5">Issued {date} · {autopay.on}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50">Amount</div>
          <div className="text-[14px] font-medium">{amount}</div>
        </div>
        {prepayable && (
          <button className="rounded-full h-9 px-4 text-[12px] border border-border bg-white hover:bg-secondary inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3"/>Prepay now
          </button>
        )}
        {!prepayable && autopay.status === "succeeded" && (
          <span className="rounded-full h-9 px-4 text-[12px] text-emerald-700 inline-flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5"/>Paid
          </span>
        )}
      </div>
    </div>
  );
}

function MatrixRow({ label, fullAuto, dailyConfirm, tone }) {
  const dot =
    tone === "emerald" ? "bg-emerald-500" :
    tone === "amber"   ? "bg-amber-500" :
                          "bg-rose-500";
  return (
    <div className="grid grid-cols-[1fr_1.5fr_1.5fr] border-b border-border last:border-0">
      <div className="px-4 py-3 flex items-center gap-2 text-[12.5px]">
        <span className={`w-2 h-2 rounded-full ${dot}`}/>
        <span>{label}</span>
      </div>
      <div className="px-4 py-3 text-[12.5px] text-foreground/80 border-l border-border">{fullAuto}</div>
      <div className="px-4 py-3 text-[12.5px] text-foreground/80 border-l border-border">{dailyConfirm}</div>
    </div>
  );
}
